import { useCallback } from 'react'
import { useSettingsStore } from '../stores/useSettingsStore'

/**
 * Drop-in replacement for useLocalStorage that reads from / writes to the
 * settings store (backed by /api/settings). Identical signature:
 *   const [value, setValue] = useSetting<T>(key, defaultValue)
 *
 * The store is hydrated before ConnectedShell mounts so reads here are
 * synchronous and safe on the first render. Missing keys return `initial`;
 * setValue serializes to JSON and write-throughs to the server.
 */
export function useSetting<T>(key: string, initial: T): [T, (value: T) => void] {
  const raw = useSettingsStore((s) => s.values[key])
  const setStore = useSettingsStore((s) => s.set)

  const value: T = (() => {
    if (raw == null) return initial
    try {
      return JSON.parse(raw) as T
    } catch {
      return initial
    }
  })()

  const set = useCallback(
    (next: T) => {
      setStore(key, JSON.stringify(next))
    },
    [key, setStore],
  )

  return [value, set]
}

/**
 * Snapshot read of a synced setting outside React (e.g. from utility modules
 * like trendRule.ts that previously poked localStorage directly).
 */
export function readSetting<T>(key: string, initial: T): T {
  const raw = useSettingsStore.getState().values[key]
  if (raw == null) return initial
  try {
    return JSON.parse(raw) as T
  } catch {
    return initial
  }
}
